import type { EvaluationRun, FormTemplate } from '../types';

/**
 * Where a form is in its life: being built, or which point its latest cycle has
 * reached. One definition for the register, the form page and the sidebar, so the
 * three can never label the same form differently.
 */
export type FormCycleState = 'draft' | 'no-cycle' | 'scheduled' | 'live' | 'closed' | 'aborted';

/** The register's status chips. A published form with no cycle only shows under All. */
export type FormCycleGroup = 'live' | 'draft' | 'closed';

export const FORM_CYCLE_LABEL: Record<FormCycleState, string> = {
  draft: 'Draft',
  'no-cycle': 'Published',
  scheduled: 'Scheduled',
  live: 'Live',
  closed: 'Closed',
  aborted: 'Aborted',
};

export function formCycleState(
  form: Pick<FormTemplate, 'status' | 'latest_run_id'>,
  run: EvaluationRun | undefined,
  now: Date = new Date(),
): FormCycleState {
  if (form.status === 'Draft') return 'draft';
  if (!form.latest_run_id || !run) return 'no-cycle';
  if (run.status === 'Cancelled') return 'aborted';
  if (run.status === 'Closed') return 'closed';
  if (run.status !== 'Published') return 'no-cycle';
  return new Date(run.start_at).getTime() > now.getTime() ? 'scheduled' : 'live';
}

export function formCycleGroup(state: FormCycleState): FormCycleGroup | null {
  if (state === 'live' || state === 'scheduled') return 'live';
  if (state === 'closed' || state === 'aborted') return 'closed';
  if (state === 'draft') return 'draft';
  return null;
}

/** A running cycle with open issues — what the admin should look at first. */
export function formNeedsAttention(state: FormCycleState, run: EvaluationRun | undefined): boolean {
  return (state === 'live' || state === 'scheduled') && (run?.exception_count ?? 0) > 0;
}
